/** @jsx jsx */
import React from 'react';
import PropTypes from 'prop-types';
import { jsx } from '@emotion/core';
import { ThemeConsumer } from '@cvent/carina-theme-provider';
import { BreakpointsContext } from '@cvent/nucleus-layout';
import { injectTestId } from '@cvent/nucleus-test-automation';
import { toMediaQuery, toMaxMediaQuery, carinaFontReset } from './styles';

const FORM_HEADING_SECTION_WIDTH = 200;

/**
 * Sections have **headings** and **content**. If `stack`-ed or below the mobile
 * threshold, the heading area is above the content. Otherwise, the heading area
 * is a fixed width column (200px) next to the content (rtl-aware).
 */
export default class FormSection extends React.Component {
  static propTypes = {
    /** Elements to be rendered in Form.Section */
    children: PropTypes.node.isRequired,
    /** Screen reader label text (`aria-label` on web) */
    accessibilityLabel: PropTypes.string,
    /** Section heading. Width in non-stacked layout is 200px (will wrap) */
    heading: PropTypes.string,
    /** Knob to hide section heading */
    hideHeading: PropTypes.bool,
    /** Right to left language preference */
    isRtl: PropTypes.bool,
    /** If stack={true}, section headings will always be on-top of form sections. */
    stack: PropTypes.bool,
    /** window-width at which FormSection should transition from a stacked-title layout, to a title-on-the-side layout */
    stackBreakpoint: PropTypes.number,
    /** Target outer container in e2e tests (`data-cvent-id` on web) */
    testID: PropTypes.string
  };

  componentDidUpdate() {
    if (process.env.NODE_ENV === 'development' && !this.props.heading) {
      // eslint-disable-next-line no-console
      console.warn(
        "You are passing an empty value to FormSection's `heading` prop. This currently renders an empty container with padding and margin present. To remove the heading element entirely, please use the `hideHeading` prop. Future versions of FormSection will automatically remove the entire heading element if its value is empty."
      );
    }
  }

  static _layoutId = 'FormSection';

  static get layoutId() {
    return FormSection._layoutId;
  }

  static contextType = BreakpointsContext;

  render() {
    const {
      testID,
      accessibilityLabel,
      stackBreakpoint,
      heading,
      hideHeading,
      isRtl,
      stack,
      css,
      style,
      classNames,
      ...propDrop
    } = this.props;

    return (
      <ThemeConsumer>
        {theme => (
          <div
            {...propDrop}
            {...injectTestId(testID)}
            css={getContainerStyles(
              theme,
              this.props,
              this.getStackBreakpoint()
            )}
            aria-label={accessibilityLabel}
          >
            {!hideHeading && this.renderHeader(theme)}
            {this.renderContent(theme)}
          </div>
        )}
      </ThemeConsumer>
    );
  }

  getStackBreakpoint = () => {
    const { stackBreakpoint } = this.props;
    if (stackBreakpoint) {
      return stackBreakpoint;
    }
    return this.context.m;
  };

  renderHeader = theme => (
    <div
      css={[
        getHeaderStyles(theme, this.props, this.getStackBreakpoint()),
        carinaFontReset(theme)
      ]}
    >
      <h3
        css={{
          fontSize: theme.font.base.size.h3,
          lineHeight: theme.font.lineHeight.heading,
          fontWeight: theme.font.base.weight.regular,
          fontFamily: theme.font.base.family,
          color: theme.font.color.base
        }}
      >
        {this.props.heading}
      </h3>
    </div>
  );

  renderContent = theme => (
    <div css={getContentStyles(theme, this.props, this.getStackBreakpoint())}>
      {this.renderRowsWithSharedProps(this.props)}
    </div>
  );

  renderRowsWithSharedProps = ({ isRtl, children }) =>
    React.Children.map(children, element =>
      React.cloneElement(element, { isRtl })
    );
}

function getContainerStyles(theme, { isRtl, stack }, stackBreakpoint) {
  return {
    display: 'flex',
    flexDirection: 'column',

    ...toMediaQuery(stackBreakpoint, {
      // eslint-disable-next-line
      flexDirection: stack ? 'column' : isRtl ? 'row-reverse' : 'row',
      borderBottomWidth: theme.size.border.base,
      borderBottomColor: theme.borderColor.base,
      borderBottomStyle: 'solid',
      ':last-child': {
        borderColor: 'transparent'
      }
    })
  };
}

function getHeaderStyles(theme, { isRtl, stack }, stackBreakpoint) {
  return {
    color: theme.font.color.base,
    padding: `${16} 0 0`,
    direction: isRtl ? 'rtl' : 'ltr',
    ...toMediaQuery(
      stackBreakpoint,
      stack ? {} : { width: FORM_HEADING_SECTION_WIDTH }
    )
  };
}

function getContentStyles(
  theme,
  { isRtl, stack, hideHeading },
  stackBreakpoint
) {
  return {
    flex: stack ? '1 0 auto' : '1 0 0%', // IE11
    paddingTop: 8,
    paddingBottom: 16,
    ...toMediaQuery(stackBreakpoint, {
      [isRtl ? 'paddingRight' : 'paddingLeft']: stack || hideHeading ? 0 : 24
    }),
    ...toMaxMediaQuery(stackBreakpoint, {
      flex: '1 0 auto' // IE11
    })
  };
}
